import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Autocomplete from '@material-ui/lab/Autocomplete'
import React from 'react'

import {
  addAptCatalogueItem,
  aptCatalogueAction,
  editAptCatalogueItem,
  fetchAptCatalogue,
  fetchAptKits,
  fetchAptVendors,
  fetchMyAptRole,
  searchMainCatalogue
} from 'api/apartmentProcurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import { useAptCategories } from './aptCategories'
import {
  NEW_CATEGORY,
  NewCategoryForm,
  NewCategoryMenuItem
} from './NewCategory'

// The At Apartment catalogue — the LXD source of truth. Two layers in one list:
//  - the seeded kit items (302 from the inventory Excel) are the pre-approved
//    base;
//  - items LXD adds on top live in ApartmentCatalogueItem and only go live once
//    Central Management (Sounak) approves them.
// Requests pull from this catalogue via "Add from catalogue" on the Requests
// tab. The same component renders on the LXD page and in the HQ catalogue view,
// so both see one identical catalogue.
// Width of the bulk-approve tick column.
const SEL_W = 36

const useStyles = makeStyles((theme) => ({
  bar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1)
  },
  spacer: { flexGrow: 1 },
  filterInput: {
    backgroundColor: '#fff',
    '& input': { fontSize: 13, padding: '7px 8px' },
    '& input::placeholder': { color: theme.palette.text.secondary, opacity: 1 }
  },
  cell: { padding: '3px 8px', fontSize: 13 },
  // The header row IS the search row (2026-08-07): the column name is the
  // input's placeholder rather than a label stacked above it, which reads as
  // two rows even inside one <tr>.
  headCell: { padding: '3px 8px', verticalAlign: 'bottom', whiteSpace: 'nowrap' },
  // Eleven columns means eleven of these. As outlined boxes they read as a
  // toolbar rather than a header, and two were narrower than their own
  // placeholder ("capex/ope"). As bare text they read as a header but stopped
  // looking like anything you could type in. So: no box, but keep the resting
  // underline — muted enough that eleven of them stay quiet, present enough
  // that every column is visibly a field. It goes solid and primary on the one
  // being typed in, which is MUI's own focus treatment.
  headInputFilled: {
    '& input': {
      color: theme.palette.text.primary,
      fontWeight: 600
    },
    // Same treatment MUI gives the focused field, kept while it holds a value:
    // an active filter is a state the header should still be showing after the
    // cursor has moved on.
    '& .MuiInput-underline:before': {
      borderBottom: `2px solid ${theme.palette.primary.main}`
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: `2px solid ${theme.palette.primary.main}`
    }
  },
  headInput: {
    '& input': { fontSize: 12, padding: '2px 0' },
    '& input::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
    '& .MuiInput-underline:before': {
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottom: `1px solid ${theme.palette.text.secondary}`
    }
  },
  // Free-text print columns overran their cell and painted over the next
  // column ("spiral binding" + "a4 80 gsm" read as one smudged string). A
  // `maxWidth` on the <td> alone does nothing in an auto-layout table — the
  // cap has to sit on a block box inside it. Full text stays on the title.
  clamp: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  // Sticky header + frozen Source/Segment/Item, mirroring HqMonthlyBoard: the
  // table is NOT inside an overflow box, so both freezes are relative to the
  // page (the document scrolls) — that is what makes position:sticky work here.
  stickyTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    },
    '& td': { boxShadow: 'inset 0 -1px 0 #eceae4' },
    // A sticky cell needs its own opaque background or scrolling content shows
    // through, so the stripe is painted on the CELLS, not the row — otherwise
    // the frozen columns stay white while the rest of the row is banded.
    '& tbody tr:nth-of-type(odd) td': { backgroundColor: theme.palette.grey[50] },
    '& tbody tr:nth-of-type(even) td': {
      backgroundColor: theme.palette.background.paper
    },
    // Pending rows keep their amber tint over the stripe.
    '& tbody tr.pendingRow td': { backgroundColor: '#fff8e1' },
    // Must stay last: equal specificity, so source order decides.
    // An unfolded game sits inside its kit, so it is tinted as one block
    // rather than picking up the zebra of the rows around it.
    '& tbody tr.gameRow td': { backgroundColor: '#faf8f4' },
    // Must stay last: equal specificity, so source order decides.
    '& tbody tr:hover td': { backgroundColor: theme.palette.action.hover }
  },
  // Segment leads now that Source is gone, so every frozen offset drops by the
  // 84px that column held (2026-08-07).
  frz2: { position: 'sticky', left: 0, zIndex: 4, width: 140, minWidth: 140 },
  frz3: {
    position: 'sticky',
    left: 140,
    zIndex: 4,
    width: 240,
    minWidth: 240,
    boxShadow: 'inset 0 -1px 0 #eceae4, 8px 0 8px -8px rgba(0,0,0,.15)'
  },
  frzHead2: { position: 'sticky', left: 0, top: 0, zIndex: 7 },
  // Tick column for bulk approve — only drawn for the head while something is
  // pending, so the frozen Segment/Item offsets shift by SEL_W only then.
  frzSel: {
    position: 'sticky',
    left: 0,
    zIndex: 4,
    width: SEL_W,
    minWidth: SEL_W,
    padding: '0 2px'
  },
  frzHeadSel: {
    position: 'sticky',
    left: 0,
    top: 0,
    zIndex: 7,
    width: SEL_W,
    minWidth: SEL_W,
    padding: '0 2px'
  },
  frzHead3: {
    position: 'sticky',
    left: 140,
    top: 0,
    zIndex: 7,
    boxShadow: 'inset 0 -1px 0 #e0ddd6, 8px 0 8px -8px rgba(0,0,0,.15)'
  },
  // Eleven search boxes widened the header, and the browser took the slack out
  // of Actions until Edit and the ⓘ stacked. It is the one column whose width
  // is not negotiable.
  actionsCell: { whiteSpace: 'nowrap', width: 1 },
  // Cost is the column the eye goes to, so it gets the tabular figures and a
  // touch more weight than the fields around it.
  costCell: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 500,
    color: theme.palette.text.primary
  },
  // The one line that always says what is on screen. It sits under the table
  // and restates the filter as money, so any slice — a kit, a budget head, a
  // typed search — is a rollup without a second view to learn.
  totalBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: -1,
    padding: '8px 12px',
    border: '1px solid #e0ddd6',
    borderTop: '2px solid',
    borderTopColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100],
    fontSize: 13
  },
  totalBig: {
    fontSize: 16,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums'
  },
  viewToggle: { marginRight: theme.spacing(1) },
  // Space goes to the column that can use it. MUI's auto layout shared the
  // width evenly, which flung four short numeric columns across ~1500px; boxing
  // the table instead just moved the waste to the right margin. So: full width,
  // numeric columns pinned narrow, and the kit name absorbing all the slack —
  // names on the left, a tight block of figures on the right.
  kitTable: { width: '100%' },
  kitNameCol: { width: '100%' },
  kitNumCol: { width: 110 },
  kitCostCol: { width: 140 },
  kitActionCol: { width: 120 },
  // The whole kit row is the toggle, so it has to read as pressable.
  kitRow: { cursor: 'pointer' },
  caret: {
    color: theme.palette.primary.main,
    marginRight: 8,
    fontSize: 11,
    display: 'inline-block',
    width: 10
  },
  // Games are one level in, and quieter than the kit that holds them.
  gameCell: { paddingLeft: 30, color: theme.palette.text.secondary },
  muted: { color: '#888' },
  partial: { color: '#9a6a00' },
  cov: { display: 'flex', alignItems: 'center', gap: 7 },
  covBar: {
    width: 46,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#e6e3dc',
    overflow: 'hidden',
    flex: 'none',
    '& i': { display: 'block', height: '100%' }
  },
  covNum: { fontSize: 11, color: '#888', fontVariantNumeric: 'tabular-nums' },
  chip: { height: 20, fontSize: 11 },
  match: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#f5f7fa',
    borderRadius: 4,
    maxHeight: 180,
    overflowY: 'auto'
  },
  matchRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    padding: '4px 0',
    cursor: 'pointer',
    borderBottom: '1px solid #eee'
  },
  field: { marginBottom: theme.spacing(1.5) }
}))

// The quantity a row is costed on — GOODS only, since 2026-08-24: LXD additions
// carry default_qty, kit items carry the numeric norm. A free-text qty_note
// ("75 ( 25 x 3 sizes )") is not a number, so those rows have no cost rather
// than a guessed one. On a print row this is the piece count, which describes
// the row but does not price it (see rowCost).
export const rowQty = (item) => {
  const q = item.status === 'base' ? item.qty_norm : item.default_qty
  const n = Number(q)
  return Number.isFinite(n) && n > 0 ? n : null
}

// Cost of ONE set of this item. Print is priced per SIDE and sides_per_file
// already holds the whole set's sides, so the card count must NOT multiply it
// again: the LXD master computes Total = Sides x PPP and never uses Quantity as
// a multiplier (Animal cards 15 cards / 30 sides / Rs 3 = Rs 90). Multiplying by
// qty as well squared the card count — `Punctuation marks` (301 cards, 602
// sides, Rs 3) read Rs 5,43,606 instead of Rs 1,806, and the catalogue totalled
// Rs 18.76L against a true Rs 1.22L (found 2026-08-24). Goods stay per unit.
// Returns null — not zero — when the row cannot be costed yet, so "not priced"
// never reads as "free".
export const rowCost = (item) => {
  const rate = Number(item.ppp)
  if (!Number.isFinite(rate) || item.ppp == null || item.ppp === '') return null
  if (item.item_type === 'print') {
    const sides = Number(item.sides_per_file)
    if (!Number.isFinite(sides) || sides <= 0) return null
    return sides * rate
  }
  const qty = rowQty(item)
  if (qty == null) return null
  return qty * rate
}

// The working behind a cost, for the cell's tooltip. Print is the one that
// needs it: sides/file x qty is the total sides, and the two are separate
// columns, so a wrong number reads as arithmetic rather than as a mystery —
// without spending a column on it.
export const costWorking = (item) => {
  const cost = rowCost(item)
  if (cost == null) return ''
  const rate = Number(item.ppp)
  if (item.item_type === 'print') {
    const sides = Number(item.sides_per_file)
    const qty = rowQty(item)
    // The card count is named, not multiplied — it is what the sides cover, and
    // seeing "301 cards" next to "602 sides" is how a wrong sides value shows up
    // as arithmetic rather than as a mystery.
    return qty
      ? `${sides} sides × ₹${rate}  (${qty} pieces)`
      : `${sides} sides × ₹${rate}`
  }
  return `${rowQty(item)} × ₹${rate}`
}

export const sumCost = (items) =>
  items.reduce((total, item) => total + (rowCost(item) || 0), 0)

// Rupees the way the rest of Alfred writes them: grouped, no decimals — the
// rates keep their paise, the totals do not need them.
const rupees = (n) => Math.round(n).toLocaleString('en-IN')

// A kit is the budget head an apartment is equipped from — the launch kits and
// the common kit are exactly the age-band categories, which is why there are
// six of them and not sixty. Segment is the GAME inside a kit ("Guess Me",
// "Shape Fusion"): it orders the catalogue so a game's pieces sit together, but
// it is not the level anyone budgets at. Rows with no category bucket together
// at the end rather than each becoming a kit of one.
const KIT_NONE = '\u0000none'
const kitKey = (item) => String(item.category || '').trim().toLowerCase() || KIT_NONE
const kitName = (item) => String(item.category || '').trim() || 'No category'

// Segment still decides the catalogue's order, so one game's items stay
// together in the list.
const SEG_NONE = '\u0000none'
const segKey = (item) => String(item.segment || '').trim().toLowerCase() || SEG_NONE

// The Kits view's own searchable columns, in its table order. Kept separate
// from COL_KEYS: these read a kit rollup, not a catalogue row.
const KIT_COL_KEYS = ['name', 'games', 'items', 'cost']

// What each Kits column is searched against. The cells show numbers, but a
// count is not a thing anyone searches for — so the games box matches the game
// NAMES inside the kit. Typing "spaggle" answers "which kit is that in?", which
// is the only question this column can usefully be asked.
const KIT_HAYSTACK = {
  name: (k) => k.name,
  games: (k) => k.gameRows.map((g) => g.name).join(' '),
  items: (k) => k.items,
  cost: (k) => rupees(k.cost)
}

// Every column that carries a search box, in table order. Actions does not.
const COL_KEYS = [
  'segment',
  'item_name',
  'reference_id',
  'category',
  'qty',
  'ppp',
  'cost',
  'vendor',
  'capex_opex',
  'item_type',
  'print_specification',
  'paper_kind',
  'sides_per_file',
  'status'
]

const STATUS_CHIP = {
  pending: { label: 'Pending Sounak', color: '#8a6d00', bg: '#ffecb3' },
  approved: { label: 'Approved', color: '#1b5e20', bg: '#d7f5da' },
  rejected: { label: 'Rejected', color: '#b71c1c', bg: '#ffdde0' },
  base: { label: 'Base', color: '#555', bg: '#eceff1' }
}

const StatusChip = ({ status, classes }) => {
  const s = STATUS_CHIP[status] || STATUS_CHIP.base
  return (
    <Chip
      className={classes.chip}
      label={s.label}
      style={{ color: s.color, backgroundColor: s.bg }}
    />
  )
}

// What a column is searched against. Two columns render something other than a
// single field, and two carry meaning the old toolbar dropdowns held, so this
// cannot just read item[key]:
//   qty     — added items show default_qty, base items show qty_norm or the
//             qty_note text. Search what the row actually shows.
//   category— "General items" is a catalogue-only grouping held on
//             catalogue_group, not a budget head; typing "general" has to find
//             it, which is what the dropped All-categories dropdown did.
//   status  — the cell shows a chip label ("Pending Sounak"), and the old
//             All-items dropdown could also isolate LXD-added rows, which is
//             `source`. All three are searchable.
const COL_HAYSTACK = {
  ppp: (i) => (i.ppp != null && i.ppp !== '' ? Number(i.ppp) : ''),
  cost: (i) => {
    const c = rowCost(i)
    // Grouped, like the cell: typing "3,600" has to find the row that shows
    // 3,600, and typing "3600" still matches on the digits either way.
    return c == null ? '' : rupees(c)
  },
  qty: (i) =>
    i.status !== 'base'
      ? i.default_qty || ''
      : i.qty_norm != null
      ? Number(i.qty_norm)
      : i.qty_note || '',
  category: (i) => `${i.category || ''} ${i.catalogue_group || ''}`,
  status: (i) =>
    `${(STATUS_CHIP[i.status] || STATUS_CHIP.base).label} ${i.status || ''} ${
      i.source || ''
    }`
}

// One column's search box. The column name is the placeholder rather than a
// label above it — the header row IS the search row.
// `fullWidth` makes the input width:100%, which contributes NOTHING to the
// column's min-content width — so an auto-layout table sizes each column off
// its BODY cells alone and a column whose data is short collapses below its own
// name: "rate ₹" rendered 8px wide as "ra", qty as "q", sides/file as "sid".
// A min-width in ch on the input puts the placeholder back into the column's
// min-content, so no column can be narrower than the word it is called.
const HeadSearch = ({ col, label, classes, value, onChange, align }) => (
  <TextField
    fullWidth
    // A filled box has to look filled. The column name is this input's
    // PLACEHOLDER, so as soon as a filter has a value the name disappears and
    // the value takes its place — in the muted tone the name was in, which
    // reads as a column heading rather than as a filter. Setting one from the
    // Kits view therefore looked like nothing had been written at all.
    className={`${classes.headInput} ${
      value[col] ? classes.headInputFilled : ''
    }`}
    placeholder={label}
    // The two numeric columns are right-aligned in the body; their header reads
    // wrong flush left.
    inputProps={{
      style: {
        minWidth: `${label.length + 1}ch`,
        ...(align === 'right' ? { textAlign: 'right' } : {})
      }
    }}
    value={value[col] || ''}
    onChange={(e) => {
      // React 16 pools events: read the value before the updater runs.
      const v = e.target.value
      onChange((f) => ({ ...f, [col]: v }))
    }}
  />
)

// ---- Segment / kit picker --------------------------------------------------
// The segment is the game or group an item belongs to and it is what the team
// searches by, but it is free text in the database — so two spellings of one
// game read as two groups, and a search for either finds only half the kit.
// Migration 0058 had to fold "Guess me" (8 items) into "Guess Me" (7) for
// exactly that reason. This offers the segments that already exist, with their
// item counts so a stray one-item segment reads as the typo it probably is, and
// still allows a genuinely new name to be typed. What it will not do is create a
// second casing of a name already in use — see snapSegment.

// Returns the spelling to store: the existing segment whenever the typed text
// matches one case-insensitively, otherwise the text as typed.
export const snapSegment = (value, known) => {
  const typed = String(value || '').trim()
  if (!typed) return ''
  const match = (known || []).find(
    (s) => String(s).toLowerCase() === typed.toLowerCase()
  )
  return match || typed
}

// options = [{ name, count }], already sorted. `variant` follows the dialog it
// sits in — Add item is outlined throughout, Edit is not.
const SegmentPicker = ({
  value,
  options,
  onChange,
  className,
  variant = 'outlined'
}) => (
  <Autocomplete
    freeSolo
    disableClearable
    fullWidth
    options={options}
    className={className}
    inputValue={value || ''}
    getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
    filterOptions={(opts, state) => {
      const typed = String(state.inputValue || '').trim()
      const needle = typed.toLowerCase()
      const shown = opts.filter((o) => o.name.toLowerCase().includes(needle))
      // Offer to create only when the text is genuinely new — an exact
      // case-insensitive hit is the existing segment, not a new one.
      if (typed && !opts.some((o) => o.name.toLowerCase() === needle)) {
        shown.push({ name: typed, count: 0, isNew: true })
      }
      return shown
    }}
    // Only the user's own typing. Autocomplete also fires this with reason
    // 'reset' — on mount, and after a pick — to sync the box from its own
    // value, which this field does not hand it; that arrives as '' and wiped
    // the segment the dialog had just seeded, so Edit opened blank and saving
    // cleared the item's segment. Controlling `value` as well is NOT the fix:
    // Autocomplete then reads the typed text as the selected value and stops
    // filtering, so every segment shows however much is typed. Both caught by
    // CatalogueTab.segment.test.js.
    onInputChange={(e, v, reason) => {
      if (reason === 'input' || reason === 'clear') onChange(v)
    }}
    onChange={(e, v) =>
      onChange(typeof v === 'string' ? v : (v && v.name) || '')
    }
    renderOption={(o) =>
      o.isNew ? (
        <Typography variant="body2" color="primary">
          + use “{o.name}” as a new segment
        </Typography>
      ) : (
        <Box
          display="flex"
          width="100%"
          alignItems="baseline"
          style={{ gap: 8 }}
        >
          <span style={{ flex: 1 }}>{o.name}</span>
          <Typography variant="caption" color="textSecondary">
            {o.count} item{o.count === 1 ? '' : 's'}
          </Typography>
        </Box>
      )
    }
    renderInput={(params) => (
      <TextField
        {...params}
        fullWidth
        variant={variant}
        size={variant === 'outlined' ? 'small' : 'medium'}
        label="Segment / kit"
      />
    )}
  />
)

// ---- Add-item dialog -------------------------------------------------------
const EMPTY = {
  item_name: '',
  category: '',
  segment: '',
  capex_opex: '',
  unit: '',
  vendor: '',
  default_qty: '',
  // Print detail (2026-08-03) — carried onto every line this item is added to.
  item_type: '',
  print_specification: '',
  paper_kind: '',
  sides_per_file: '',
  reference_link: '',
  file_link: '',
  notes: ''
}

const AddItemDialog = ({ open, onClose, onAdded, segmentOptions, vendorOptions }) => {
  const classes = useStyles()
  const categories = useAptCategories()
  const [addingCategory, setAddingCategory] = React.useState(false)
  const [form, setForm] = React.useState(EMPTY)
  const [matchQuery, setMatchQuery] = React.useState('')
  const [matches, setMatches] = React.useState([])
  const [picked, setPicked] = React.useState(null) // {reference_id, name, ...}
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setForm(EMPTY)
      setAddingCategory(false)
      setMatchQuery('')
      setMatches([])
      setPicked(null)
    }
  }, [open])

  // Debounced search of the MAIN catalogue for a similar item to reuse its id.
  React.useEffect(() => {
    const q = matchQuery.trim()
    if (!q) {
      setMatches([])
      return undefined
    }
    const t = setTimeout(() => {
      searchMainCatalogue(q)
        .then((rows) => setMatches(rows || []))
        .catch(() => setMatches([]))
    }, 300)
    return () => clearTimeout(t)
  }, [matchQuery])

  const set = (key) => (e) => {
    const value = e.target.value
    setForm((f) => ({ ...f, [key]: value }))
  }

  const pick = (row) => {
    setPicked(row)
    setMatches([])
    setForm((f) => ({
      ...f,
      reference_link: f.reference_link || row.reference_link || '',
      category: f.category || row.category || ''
    }))
  }

  const submit = async () => {
    if (!form.item_name.trim()) {
      eventAlert('Item name is required.', constants.ERROR, 'Add item')
      return
    }
    setSaving(true)
    try {
      const added = await addAptCatalogueItem({
        ...form,
        segment: snapSegment(
          form.segment,
          (segmentOptions || []).map((o) => o.name)
        ),
        matched_reference_id: picked ? picked.reference_id : '',
        matched_catalogue_name: picked ? picked.name : ''
      })
      eventAlert(
        added && added.reference_id
          ? `Item added as ${added.reference_id} — awaiting Sounak’s approval.`
          : 'Item added — awaiting Sounak’s approval.',
        constants.SUCCESS,
        'Catalogue'
      )
      onAdded()
      onClose()
    } catch (error) {
      eventAlert(
        error?.message || 'Could not add the item.',
        constants.ERROR,
        'Add item'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add item to the At Apartment catalogue</DialogTitle>
      <DialogContent>
        <Typography variant="caption" className={classes.muted}>
          The item gets its reference ID as soon as it is added, and joins the
          live catalogue once Sounak approves it.
        </Typography>
        <Box mt={1.5}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label="Item name *"
            value={form.item_name}
            onChange={set('item_name')}
          />
          <Box display="flex" style={{ gap: 8 }}>
            <TextField
              className={classes.field}
              fullWidth
              variant="outlined"
              size="small"
              select
              label="Category"
              value={form.category}
              onChange={(e) =>
                e.target.value === NEW_CATEGORY
                  ? setAddingCategory(true)
                  : set('category')(e)
              }>
              <MenuItem value="">(none)</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
              <NewCategoryMenuItem value={NEW_CATEGORY} />
            </TextField>
            <TextField
              className={classes.field}
              fullWidth
              variant="outlined"
              size="small"
              select
              label="Capex / Opex"
              value={form.capex_opex}
              onChange={set('capex_opex')}>
              <MenuItem value="">(none)</MenuItem>
              <MenuItem value="capex">Capex</MenuItem>
              <MenuItem value="opex">Opex</MenuItem>
            </TextField>
          </Box>
          {addingCategory && (
            <NewCategoryForm
              onCancel={() => setAddingCategory(false)}
              onCreated={(name) => {
                setAddingCategory(false)
                setForm((f) => ({ ...f, category: name }))
              }}
            />
          )}
          <Box display="flex" style={{ gap: 8 }}>
            <SegmentPicker
              className={classes.field}
              value={form.segment}
              options={segmentOptions || []}
              onChange={(v) => setForm((f) => ({ ...f, segment: v }))}
            />
            <TextField
              className={classes.field}
              fullWidth
              variant="outlined"
              size="small"
              label="Unit"
              value={form.unit}
              onChange={set('unit')}
            />
            <TextField
              className={classes.field}
              fullWidth
              type="number"
              variant="outlined"
              size="small"
              label="Required qty"
              helperText={
                form.item_type === 'print'
                  ? 'Pieces in one set — the sides carry the cost'
                  : 'Number ordered when launching'
              }
              value={form.default_qty}
              onChange={set('default_qty')}
            />
            <TextField
              className={classes.field}
              fullWidth
              type="number"
              variant="outlined"
              size="small"
              label="Rate (₹)"
              helperText="Per unit for goods; per side for print"
              value={form.ppp || ''}
              onChange={set('ppp')}
            />
          </Box>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            select
            label="Vendor"
            helperText="Who HQ usually orders this from — autofills order lines"
            value={form.vendor}
            onChange={set('vendor')}>
            <MenuItem value="">(none)</MenuItem>
            {(vendorOptions || []).map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </TextField>
          {/* Print detail. A print item with no spec reaches the vendor
              unbuildable, and with no Type it prices as goods (2026-08-03). */}
          <Box display="flex" style={{ gap: 8 }}>
            <TextField
              className={classes.field}
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Type"
              value={form.item_type}
              onChange={set('item_type')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="goods">Goods</MenuItem>
              <MenuItem value="print">Print</MenuItem>
            </TextField>
            {form.item_type === 'print' && (
              <TextField
                className={classes.field}
                fullWidth
                type="number"
                variant="outlined"
                size="small"
                label="Sides per file"
                value={form.sides_per_file}
                onChange={set('sides_per_file')}
              />
            )}
          </Box>
          {form.item_type === 'print' && (
            <>
              <TextField
                className={classes.field}
                fullWidth
                multiline
                variant="outlined"
                size="small"
                label="Print specification"
                helperText="e.g. colour, front & back, curved edges"
                value={form.print_specification}
                onChange={set('print_specification')}
              />
              <TextField
                className={classes.field}
                fullWidth
                variant="outlined"
                size="small"
                label="Kind of paper"
                helperText="e.g. A7, 300gsm"
                value={form.paper_kind}
                onChange={set('paper_kind')}
              />
            </>
          )}
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label="Reference link"
            value={form.reference_link}
            onChange={set('reference_link')}
          />
          {/* A print item is a FRONT and a BACK — two files (2026-09-02). The
              source sheet has always held both in one cell; the catalogue only
              had room for one, so every back artwork was dropped on import. */}
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label="Second file (back artwork)"
            value={form.file_link}
            onChange={set('file_link')}
          />

          {/* Reference-id matching against the main catalogue. */}
          {picked ? (
            <Box className={classes.field}>
              <Typography variant="body2">
                Reusing main-catalogue id{' '}
                <b>{picked.reference_id || '—'}</b> — {picked.name}
                <Button
                  size="small"
                  onClick={() => setPicked(null)}
                  style={{ marginLeft: 8 }}>
                  change
                </Button>
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Match to main catalogue (reuse its reference id)"
                placeholder="Search main catalogue by name / id…"
                value={matchQuery}
                onChange={(e) => setMatchQuery(e.target.value)}
              />
              {matches.length > 0 && (
                <Box className={classes.match}>
                  {matches.map((m) => (
                    <div
                      key={m.reference_id || m.name}
                      className={classes.matchRow}
                      onClick={() => pick(m)}>
                      <span>{m.name}</span>
                      <span className={classes.muted}>
                        {m.reference_id || '—'}
                      </span>
                    </div>
                  ))}
                </Box>
              )}
              {matchQuery.trim() && matches.length === 0 && (
                <Typography variant="caption" className={classes.muted}>
                  No match — a new AA_CT id is minted when Sounak approves.
                </Typography>
              )}
            </>
          )}

          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label="Notes (optional)"
            multiline
            value={form.notes}
            onChange={set('notes')}
            style={{ marginTop: 8 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={submit}
          disabled={saving}>
          Add item
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---- Review dialog (Sounak approves / rejects) -----------------------------
const ReviewDialog = ({ item, onClose, onReviewed }) => {
  const [note, setNote] = React.useState('')
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setNote('')
  }, [item])

  const act = async (action) => {
    setSaving(true)
    try {
      await aptCatalogueAction(item.uuid, action, note)
      eventAlert(
        action === 'approve' ? 'Item approved.' : 'Item rejected.',
        constants.SUCCESS,
        'Catalogue'
      )
      onReviewed()
      onClose()
    } catch (error) {
      eventAlert(
        error?.message || 'Could not save.',
        constants.ERROR,
        'Review'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Review catalogue item</DialogTitle>
      <DialogContent>
        {item && (
          <>
            <Typography variant="body2" paragraph>
              <b>{item.item_name}</b>
              {item.category ? ` · ${item.category}` : ''}
              {item.reference_id ? ` · ${item.reference_id}` : ''}
            </Typography>
            {item.notes && (
              <Typography variant="body2" color="textSecondary" paragraph>
                LXD note: {item.notes}
              </Typography>
            )}
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Review note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => act('reject')}
          disabled={saving}
          style={{ color: '#b71c1c' }}>
          Reject
        </Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={() => act('approve')}
          disabled={saving}>
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---- Catalogue tab ---------------------------------------------------------
// ---- Edit dialog (edit an existing catalogue item's fields) ----------------
// ---- Bulk approve (Sounak ticks several pending items) ---------------------
// One call per item through the same approve endpoint the Review dialog uses,
// so every item is checked and stamped exactly as a single approve would be.
const BulkApproveDialog = ({ items, open, onClose, onDone }) => {
  const [note, setNote] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [done, setDone] = React.useState(0)

  React.useEffect(() => {
    if (open) {
      setNote('')
      setDone(0)
    }
  }, [open])

  const approveAll = async () => {
    setSaving(true)
    const failed = []
    for (const item of items) {
      try {
        await aptCatalogueAction(item.uuid, 'approve', note)
      } catch (error) {
        failed.push(item.item_name)
      }
      setDone((d) => d + 1)
    }
    setSaving(false)
    const ok = items.length - failed.length
    if (ok) {
      eventAlert(
        `${ok} item${ok === 1 ? '' : 's'} approved.`,
        constants.SUCCESS,
        'Catalogue'
      )
    }
    if (failed.length) {
      eventAlert(
        `Could not approve: ${failed.join(', ')}`,
        constants.ERROR,
        'Approve'
      )
    }
    onDone()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        Approve {items.length} item{items.length === 1 ? '' : 's'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          These join the live catalogue. Items without a reference ID get a new
          AA_CT id.
        </Typography>
        <Box
          mb={2}
          style={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid #e0ddd6',
            borderRadius: 4
          }}>
          {items.map((item) => (
            <Box
              key={item.uuid}
              px={1.5}
              py={0.75}
              display="flex"
              style={{ gap: 8, borderBottom: '1px solid #f0eee8' }}>
              <Typography variant="body2" style={{ flex: 1 }}>
                {item.item_name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {item.segment || '—'}
                {item.reference_id ? ` · ${item.reference_id}` : ''}
              </Typography>
            </Box>
          ))}
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          id="bulk-approve-note"
          label="Review note for all (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={saving}
        />
        {saving && (
          <Box mt={2}>
            <LinearProgress
              variant="determinate"
              value={items.length ? (done / items.length) * 100 : 0}
            />
            <Typography variant="caption" color="textSecondary">
              {done} of {items.length} done
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={approveAll}
          disabled={saving || !items.length}>
          Approve {items.length}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const EditDialog = ({ item, onClose, onSaved, segmentOptions, vendorOptions }) => {
  const categories = useAptCategories()
  const [addingCategory, setAddingCategory] = React.useState(false)
  const [form, setForm] = React.useState({})
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    setAddingCategory(false)
    if (item) {
      setForm({
        item_name: item.item_name || '',
        category: item.category || '',
        segment: item.segment || '',
        unit: item.unit || '',
        vendor: item.vendor || '',
        default_qty: item.default_qty != null ? item.default_qty : '',
        ppp: item.ppp != null ? item.ppp : '',
        item_type: item.item_type || '',
        print_specification: item.print_specification || '',
        paper_kind: item.paper_kind || '',
        sides_per_file: item.sides_per_file != null ? item.sides_per_file : '',
        reference_id: item.reference_id || '',
        reference_link: item.reference_link || '',
        file_link: item.file_link || '',
        notes: item.notes || ''
      })
    }
  }, [item])

  // Read the value BEFORE the updater: React 16 recycles the event once the
  // handler returns, so `e.target` inside `setForm((f) => …)` can already be
  // null — typing a new Required qty crashed the page on prod (2026-09-17).
  const set = (k) => (e) => {
    const value = e.target.value
    setForm((f) => ({ ...f, [k]: value }))
  }

  const save = async () => {
    if (!String(form.item_name || '').trim()) {
      eventAlert('Name is required.', constants.ERROR, 'Edit')
      return
    }
    setSaving(true)
    try {
      await editAptCatalogueItem(item.uuid, {
        ...form,
        segment: snapSegment(
          form.segment,
          (segmentOptions || []).map((o) => o.name)
        )
      })
      eventAlert('Item updated.', constants.SUCCESS, 'Catalogue')
      onSaved()
      onClose()
    } catch (e) {
      eventAlert((e && e.message) || 'Could not save.', constants.ERROR, 'Edit')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit catalogue item</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
          <TextField label="Name" fullWidth value={form.item_name || ''} onChange={set('item_name')} />
          <TextField
            select
            label="Category"
            fullWidth
            value={form.category || ''}
            onChange={(e) =>
              e.target.value === NEW_CATEGORY
                ? setAddingCategory(true)
                : set('category')(e)
            }>
            <MenuItem value="">—</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
            <NewCategoryMenuItem value={NEW_CATEGORY} />
          </TextField>
          {addingCategory && (
            <NewCategoryForm
              onCancel={() => setAddingCategory(false)}
              onCreated={(name) => {
                setAddingCategory(false)
                setForm((f) => ({ ...f, category: name }))
              }}
            />
          )}
          <SegmentPicker
            variant="standard"
            value={form.segment}
            options={segmentOptions || []}
            onChange={(v) => setForm((f) => ({ ...f, segment: v }))}
          />
          <TextField label="Unit" value={form.unit || ''} onChange={set('unit')} />
          <TextField
            select
            label="Vendor"
            helperText="Who HQ usually orders this from — autofills order lines"
            value={form.vendor || ''}
            onChange={set('vendor')}>
            <MenuItem value="">(none)</MenuItem>
            {(form.vendor && !(vendorOptions || []).includes(form.vendor)
              ? [form.vendor, ...(vendorOptions || [])]
              : vendorOptions || []
            ).map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            label="Required qty"
            helperText={
              form.item_type === 'print'
                ? 'Pieces in one set — the sides carry the cost'
                : 'Number ordered when launching a category'
            }
            value={form.default_qty ?? ''}
            onChange={set('default_qty')}
          />
          <TextField type="number" label="Rate (₹)" helperText="Per unit for goods; per side for print" value={form.ppp ?? ''} onChange={set('ppp')} />
          {/* Print detail (2026-08-03). These ride onto every line the item is
              added to — a print item with no spec reaches the vendor unbuildable,
              and with no Type it prices as goods. */}
          <TextField select label="Type" value={form.item_type || ''} onChange={set('item_type')} helperText="Print lines price on sides x rate; goods on units x rate">
            <MenuItem value="">—</MenuItem>
            <MenuItem value="goods">Goods</MenuItem>
            <MenuItem value="print">Print</MenuItem>
          </TextField>
          {form.item_type === 'print' && (
            <>
              <TextField label="Print specification" fullWidth multiline value={form.print_specification || ''} onChange={set('print_specification')} helperText="e.g. colour, front & back, curved edges" />
              <TextField label="Kind of paper" value={form.paper_kind || ''} onChange={set('paper_kind')} helperText="e.g. A7, 300gsm" />
              <TextField type="number" label="Sides per file" value={form.sides_per_file ?? ''} onChange={set('sides_per_file')} />
            </>
          )}
          <TextField label="Reference ID" value={form.reference_id || ''} onChange={set('reference_id')} />
          <TextField label="File / purchase link" fullWidth value={form.reference_link || ''} onChange={set('reference_link')} />
          <TextField
            label="Second file (back artwork)"
            fullWidth
            value={form.file_link || ''}
            onChange={set('file_link')}
          />
          <TextField label="Notes" fullWidth multiline value={form.notes || ''} onChange={set('notes')} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="primary" variant="contained" disableElevation disabled={saving} onClick={save}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const CatalogueTab = () => {
  const classes = useStyles()
  const [kits, setKits] = React.useState(null)
  const [additions, setAdditions] = React.useState(null)
  const [role, setRole] = React.useState(null)
  const [search, setSearch] = React.useState('')
  // 'items' is the catalogue as it always was, with cost per row and a subtotal
  // where each kit ends. 'kits' collapses it to one row per kit so kits can be
  // compared — the one thing a filter cannot do, since a filter shows one kit
  // at a time.
  const [view, setView] = React.useState('items')
  // One kit open at a time: sixty-odd games unfolded at once is the wall this
  // view exists to avoid, and comparing kits is what the collapsed rows are for.
  const [openKit, setOpenKit] = React.useState(null)
  const [kitFilters, setKitFilters] = React.useState(
    KIT_COL_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {})
  )
  // {item, anchor} for the ⓘ popover that replaced the Source column.
  const [info, setInfo] = React.useState(null)
  // Per-column search, in the header cells themselves — every column except
  // Actions. The two toolbar dropdowns this replaces (All categories / All
  // items) were a second place to look for the same thing; what they could
  // express that a substring cannot is folded into COL_HAYSTACK below.
  const [colFilters, setColFilters] = React.useState(
    COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), {})
  )
  const [addOpen, setAddOpen] = React.useState(false)
  const [reviewItem, setReviewItem] = React.useState(null)
  // Pending items the head has ticked for bulk approve (uuids).
  const [ticked, setTicked] = React.useState([])
  const [bulkOpen, setBulkOpen] = React.useState(false)
  const [editItem, setEditItem] = React.useState(null)

  const loadAdditions = React.useCallback(() => {
    fetchAptCatalogue().then(setAdditions).catch(() => setAdditions([]))
  }, [])

  // Approved vendor names for the dialogs' Vendor pick — the line dropdowns
  // accept exactly this list, so the catalogue holds nothing they can't show.
  const [vendorNames, setVendorNames] = React.useState([])

  React.useEffect(() => {
    fetchAptVendors({ status: 'approved' })
      .then((vs) => setVendorNames((vs || []).map((v) => v.name)))
      .catch(() => setVendorNames([]))
  }, [])

  React.useEffect(() => {
    fetchAptKits(true).then(setKits)
    loadAdditions()
    fetchMyAptRole()
      .then(setRole)
      .catch(() => setRole({ can_request: false, is_central: false }))
  }, [loadAdditions])

  if (!kits || !additions) return <LinearProgress />

  const canRequest = !!(role && role.can_request)
  const isCentral = !!(role && role.is_central)
  // Who can edit an existing item: LXD, the head, or HQ (matches the backend).
  const canEdit = !!(role && (role.can_request || role.is_central || role.is_hq))

  // Base layer: every kit item, flattened.
  const baseItems = kits.flatMap((kit) =>
    (kit.items || []).map((item) => ({
      key: item.uuid,
      source: kit.name,
      status: 'base',
      segment: item.segment,
      item_name: item.item_name,
      reference_id: item.reference_id,
      category: item.category,
      qty_norm: item.qty_norm,
      qty_note: item.qty_note,
      norm_basis: item.norm_basis,
      capex_opex: item.capex_opex,
      reference_link: item.reference_link,
      // ⛔ This grid renders a HAND-MAPPED row, not the API object, so a new
      // field is invisible until it is named here — the serializer returning
      // it is not enough (2026-09-02).
      file_link: item.file_link
    }))
  )
  // Addition layer: LXD-added items (pending / approved / rejected).
  const addedItems = additions.map((a) => ({
    key: a.uuid,
    uuid: a.uuid,
    source: 'LXD added',
    status: a.status,
    segment: a.segment,
    item_name: a.item_name,
    reference_id: a.reference_id,
    category: a.category,
    qty_norm: null,
    qty_note: '',
    norm_basis: '',
    default_qty: a.default_qty,
    // The rate has to be carried through explicitly like every other field:
    // this projection is a whitelist, and leaving ppp out of it made the whole
    // rate column render "—" even with the value in the payload — and, worse,
    // opened the Edit dialog with a blank Rate, which saves back as null.
    ppp: a.ppp,
    capex_opex: a.capex_opex,
    catalogue_group: a.catalogue_group || '',
    item_type: a.item_type,
    print_specification: a.print_specification,
    paper_kind: a.paper_kind,
    sides_per_file: a.sides_per_file,
    reference_link: a.reference_link,
    file_link: a.file_link,
    unit: a.unit,
    vendor: a.vendor || '',
    notes: a.notes,
    added_by_name: a.added_by_name,
    reviewed_by_name: a.reviewed_by_name,
    reviewed_at: a.reviewed_at,
    review_note: a.review_note,
    modified: a.modified,
    matched_catalogue_name: a.matched_catalogue_name
  }))

  const pendingCount = addedItems.filter((i) => i.status === 'pending').length

  // Sorted by kit, then item name, with un-kitted rows last. This has to happen
  // on the MERGED list: the table stitches LXD additions together with kit
  // items, so ordering either source server-side still leaves the two blocks
  // stacked rather than interleaved — and the kit subtotals below depend on
  // every row of a kit being contiguous.
  const all = [...addedItems, ...baseItems].sort((a, b) => {
    const sa = segKey(a)
    const sb = segKey(b)
    if (sa !== sb) {
      if (sa === SEG_NONE) return 1
      if (sb === SEG_NONE) return -1
      return sa.localeCompare(sb)
    }
    return String(a.item_name || '').localeCompare(String(b.item_name || ''))
  })

  // Every segment in use, with how many items sit under it — the option list
  // both dialogs pick from. Built off the whole catalogue, not the filtered
  // rows, so a segment does not disappear from the picker because a category
  // filter is on. Counted case-insensitively but shown under the spelling most
  // items already use, which is the one snapSegment will store.
  const segmentOptions = Object.values(
    all.reduce((acc, item) => {
      const name = String(item.segment || '').trim()
      if (!name) return acc
      const key = name.toLowerCase()
      if (!acc[key]) acc[key] = { name, count: 0, spellings: {} }
      acc[key].count += 1
      acc[key].spellings[name] = (acc[key].spellings[name] || 0) + 1
      if (acc[key].spellings[name] > (acc[key].spellings[acc[key].name] || 0)) {
        acc[key].name = name
      }
      return acc
    }, {})
  )
    .map(({ name, count }) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const headProps = { classes, value: colFilters, onChange: setColFilters }
  const needle = search.trim().toLowerCase()
  const rows = all.filter((item) => {
    if (
      needle &&
      !`${item.item_name} ${item.segment || ''} ${item.reference_id || ''}`
        .toLowerCase()
        .includes(needle)
    )
      return false
    return COL_KEYS.every((key) => {
      const q = String(colFilters[key] || '').trim().toLowerCase()
      if (!q) return true
      const source = COL_HAYSTACK[key] || ((i) => i[key])
      const hay = String(source(item) || '').toLowerCase()
      // "live" was a dropdown option, not a value on any row: base + approved,
      // i.e. everything the catalogue actually offers. Kept as a whole word the
      // Status box understands, so dropping the dropdown loses nothing. Matched
      // exactly rather than by prefix — otherwise "l" would hijack the search
      // for "LXD added".
      if (key === 'status' && q === 'live') {
        return item.status === 'base' || item.status === 'approved'
      }
      return hay.includes(q)
    })
  })

  // Bulk approve: the head ticks pending rows. Only pending rows that are
  // still pending count — a tick on an item approved meanwhile drops out.
  const showTicks = isCentral && pendingCount > 0
  const pendingShown = rows.filter((i) => i.status === 'pending')
  const tickedItems = addedItems.filter(
    (i) => i.status === 'pending' && ticked.includes(i.uuid)
  )
  const allShownTicked =
    pendingShown.length > 0 &&
    pendingShown.every((i) => ticked.includes(i.uuid))
  const toggleTick = (uuid) =>
    setTicked((t) =>
      t.includes(uuid) ? t.filter((u) => u !== uuid) : [...t, uuid]
    )
  const toggleAllShown = () => {
    const shownIds = pendingShown.map((i) => i.uuid)
    setTicked((t) =>
      allShownTicked
        ? t.filter((u) => !shownIds.includes(u))
        : [...new Set([...t, ...shownIds])]
    )
  }
  // Segment and Item are frozen from the left edge; the tick column pushes them.
  const selOffset = showTicks ? SEL_W : 0

  // One entry per kit in the CURRENT view, in the order the rows already sit —
  // so the summary and the table can never disagree about what is on screen.
  const kitRollup = []
  const kitIndex = {}
  rows.forEach((item) => {
    const key = kitKey(item)
    if (kitIndex[key] === undefined) {
      kitIndex[key] = kitRollup.length
      kitRollup.push({
        key,
        name: kitName(item),
        // The games inside the kit, each with its own tally, so the kit can
        // unfold one level without a second pass over the catalogue.
        segments: {},
        items: 0,
        costable: 0,
        cost: 0
      })
    }
    const kit = kitRollup[kitIndex[key]]
    kit.items += 1
    const cost = rowCost(item)
    if (cost != null) {
      kit.costable += 1
      kit.cost += cost
    }
    const seg = String(item.segment || '').trim()
    const segLabel = seg || 'No game'
    if (!kit.segments[segLabel]) {
      kit.segments[segLabel] = { name: segLabel, named: !!seg, items: 0, cost: 0 }
    }
    kit.segments[segLabel].items += 1
    if (cost != null) kit.segments[segLabel].cost += cost
  })
  kitRollup.forEach((kit) => {
    // Games read alphabetically, NOT by cost. Cost order split families apart —
    // inside 5+ Robotics it put "Manuals- L1" and "Manual L2" four rows apart,
    // and separated the two "Experiment …" games — because what a thing costs
    // says nothing about what it belongs with. The shared prefix IS the family,
    // so sorting on the name keeps L1 next to L2. The kits themselves stay in
    // cost order: that list is a summary, and there are no families in it.
    kit.gameRows = Object.values(kit.segments).sort((a, b) => {
      // A row with no game at all is an absence, so it sits at the end rather
      // than under N.
      if (a.named !== b.named) return a.named ? -1 : 1
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    })
    kit.games = kit.gameRows.length
  })
  const kitHeadProps = { classes, value: kitFilters, onChange: setKitFilters }
  const gameQuery = String(kitFilters.games || '').trim().toLowerCase()
  // Both boxes, so the catalogue lands on that game inside that kit rather than
  // on every kit's game of the same name.
  const openGame = (kit, game) => {
    setColFilters((f) => ({
      ...f,
      category: kit.name === 'No category' ? '' : kit.name,
      segment: game.name
    }))
    setView('items')
  }
  const activeFilters =
    (search.trim() ? 1 : 0) +
    COL_KEYS.filter((k) => String(colFilters[k] || '').trim()).length +
    KIT_COL_KEYS.filter((k) => String(kitFilters[k] || '').trim()).length
  const clearFilters = () => {
    setSearch('')
    setColFilters(COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), {}))
    setKitFilters(KIT_COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), {}))
  }
  // The Kits view's own filter pass, over the rollup the item filters already
  // produced — so the two views never contradict each other. Dearest first,
  // because "where is the money" is the question this view answers.
  const kitsShown = kitRollup
    .filter((kit) =>
      KIT_COL_KEYS.every((key) => {
        const q = String(kitFilters[key] || '').trim().toLowerCase()
        if (!q) return true
        return String(KIT_HAYSTACK[key](kit) || '').toLowerCase().includes(q)
      })
    )
    .sort((a, b) => b.cost - a.cost)

  // What the bar totals is whatever the view is showing: the filtered kits when
  // the Kits view is on, the filtered rows otherwise.
  const counted = view === 'kits' ? kitsShown : kitRollup
  const shownCostable = counted.reduce((n, k) => n + k.costable, 0)
  const shownCost = counted.reduce((n, k) => n + k.cost, 0)
  const shownItems = counted.reduce((n, k) => n + k.items, 0)

  return (
    <Box>
      <Box className={classes.bar}>
        <TextField
          variant="outlined"
          size="small"
          className={classes.filterInput}
          placeholder="Search items…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* Two readings of one list, not two pages: items is the catalogue,
            kits is the same rows collapsed one per kit so they can be compared.
            Filters and search apply to both. */}
        <ButtonGroup size="small" className={classes.viewToggle}>
          <Button
            variant={view === 'items' ? 'contained' : 'outlined'}
            color={view === 'items' ? 'primary' : 'default'}
            disableElevation
            onClick={() => setView('items')}>
            Items
          </Button>
          <Button
            variant={view === 'kits' ? 'contained' : 'outlined'}
            color={view === 'kits' ? 'primary' : 'default'}
            disableElevation
            onClick={() => setView('kits')}>
            Kits
          </Button>
        </ButtonGroup>
        {/* A filter can arrive without being typed — Open → on a kit sets one —
            so there has to be a way back that does not require finding which
            of thirteen boxes is holding it. */}
        {activeFilters > 0 && (
          <Chip
            className={classes.chip}
            label={`${activeFilters} filter${
              activeFilters > 1 ? 's' : ''
            } · clear`}
            onDelete={clearFilters}
            onClick={clearFilters}
            variant="outlined"
            color="primary"
          />
        )}
        {isCentral && pendingCount > 0 && (
          <Chip
            className={classes.chip}
            label={`${pendingCount} pending review`}
            style={{ color: '#8a6d00', backgroundColor: '#ffecb3' }}
            // The shortcut now fills the Status column's own search box, so the
            // chip and the header agree on what is being shown.
            onClick={() =>
              setColFilters((f) => ({ ...f, status: 'pending' }))
            }
          />
        )}
        {isCentral && tickedItems.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            onClick={() => setBulkOpen(true)}>
            ✓ Approve selected ({tickedItems.length})
          </Button>
        )}
        <span className={classes.spacer} />
        <Typography variant="caption" className={classes.muted}>
          {rows.length} of {all.length}
        </Typography>
        {canRequest && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            onClick={() => setAddOpen(true)}>
            + Add item
          </Button>
        )}
      </Box>
      {view === 'kits' && (
        <Paper variant="outlined">
          <Table
            size="small"
            className={`${classes.stickyTable} ${classes.kitTable}`}>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.headCell} ${classes.kitNameCol}`}>
                  <HeadSearch col="name" label="kit" {...kitHeadProps} />
                </TableCell>
                <TableCell className={`${classes.headCell} ${classes.kitNumCol}`}>
                  <HeadSearch
                    col="games"
                    label="games"
                    align="right"
                    {...kitHeadProps}
                  />
                </TableCell>
                <TableCell className={`${classes.headCell} ${classes.kitNumCol}`}>
                  <HeadSearch
                    col="items"
                    label="items"
                    align="right"
                    {...kitHeadProps}
                  />
                </TableCell>
                <TableCell className={`${classes.headCell} ${classes.kitCostCol}`}>
                  <HeadSearch
                    col="cost"
                    label="cost ₹"
                    align="right"
                    {...kitHeadProps}
                  />
                </TableCell>
                <TableCell className={`${classes.headCell} ${classes.kitActionCol}`} />
              </TableRow>
            </TableHead>
            <TableBody>
              {kitsShown.map((kit) => (
                <React.Fragment key={kit.key}>
                <TableRow
                  hover
                  className={classes.kitRow}
                  onClick={() =>
                    setOpenKit(openKit === kit.key ? null : kit.key)
                  }>
                  <TableCell className={classes.cell}>
                    <span className={classes.caret}>
                      {openKit === kit.key ? '▾' : '▸'}
                    </span>
                    {kit.name}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {kit.games || '—'}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {kit.items}
                  </TableCell>
                  <TableCell
                    className={`${classes.cell} ${classes.costCell}`}
                    align="right"
                    // Every item is meant to carry a rate, so coverage is not a
                    // column and not a colour: most kits are short today, and
                    // tinting them all would make the exception the rule. The
                    // bar states the gap once; this names it per kit on hover.
                    title={
                      kit.costable < kit.items
                        ? `${kit.costable} of ${kit.items} items have a rate and a quantity`
                        : ''
                    }>
                    {rupees(kit.cost)}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {/* Comparing kits is what this view is for; the moment you
                        want the items behind one, it hands you back to the
                        catalogue already filtered to it. */}
                    <Button
                      size="small"
                      onClick={(e) => {
                        // The row toggles; the button drills through. Without
                        // this the click would do both.
                        e.stopPropagation()
                        setColFilters((f) => ({
                          ...f,
                          category: kit.name === 'No category' ? '' : kit.name
                        }))
                        setView('items')
                      }}>
                      Open →
                    </Button>
                  </TableCell>
                </TableRow>
                {/* A games search would otherwise narrow to a kit without
                    showing WHICH game matched — the silent-filter trap. So a
                    matching kit opens itself, on just the games that matched. */}
                {(openKit === kit.key || gameQuery) &&
                  kit.gameRows
                    .filter(
                      (g) =>
                        !gameQuery || g.name.toLowerCase().includes(gameQuery)
                    )
                    .map((game) => (
                    <TableRow
                      key={`${kit.key}/${game.name}`}
                      className="gameRow"
                      hover
                      // The kit row opens on click, so the game row has to as
                      // well — a button that is the only live target in a row
                      // full of its own data reads as broken.
                      style={game.named ? { cursor: 'pointer' } : undefined}
                      onClick={
                        game.named ? () => openGame(kit, game) : undefined
                      }>
                      <TableCell className={`${classes.cell} ${classes.gameCell}`}>
                        {game.name}
                      </TableCell>
                      <TableCell className={classes.cell} />
                      <TableCell className={classes.cell} align="right">
                        {game.items}
                      </TableCell>
                      <TableCell
                        className={`${classes.cell} ${classes.costCell}`}
                        align="right">
                        {rupees(game.cost)}
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        <Button
                          size="small"
                          disabled={!game.named}
                          onClick={(e) => {
                            e.stopPropagation()
                            openGame(kit, game)
                          }}>
                          Open →
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
              {!kitsShown.length && (
                <TableRow>
                  <TableCell colSpan={5} className={classes.cell}>
                    <Typography variant="body2" className={classes.muted}>
                      No kits match.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
      {view === 'items' && (
      <Paper variant="outlined">
        <Table size="small" className={classes.stickyTable}>
          <TableHead>
            <TableRow>
              {showTicks && (
                <TableCell
                  className={`${classes.headCell} ${classes.frzHeadSel}`}>
                  <Checkbox
                    size="small"
                    color="primary"
                    title="Tick every pending item shown"
                    disabled={!pendingShown.length}
                    checked={allShownTicked}
                    indeterminate={
                      !allShownTicked &&
                      pendingShown.some((i) => ticked.includes(i.uuid))
                    }
                    onChange={toggleAllShown}
                  />
                </TableCell>
              )}
              <TableCell
                className={`${classes.headCell} ${classes.frzHead2}`}
                style={{ left: selOffset }}>
                <HeadSearch col="segment" label="segment" {...headProps} />
              </TableCell>
              <TableCell
                className={`${classes.headCell} ${classes.frzHead3}`}
                style={{ left: 140 + selOffset }}>
                <HeadSearch col="item_name" label="item" {...headProps} />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch
                  col="reference_id"
                  label="reference id"
                  {...headProps}
                />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch col="category" label="category" {...headProps} />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch
                  col="qty"
                  label="qty"
                  align="right"
                  {...headProps}
                />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch col="ppp" label="rate ₹" align="right" {...headProps} />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch col="cost" label="cost ₹" align="right" {...headProps} />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch col="vendor" label="vendor" {...headProps} />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch
                  col="capex_opex"
                  label="capex/opex"
                  {...headProps}
                />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch col="item_type" label="type" {...headProps} />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch
                  col="print_specification"
                  label="print specification"
                  {...headProps}
                />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch
                  col="paper_kind"
                  label="kind of paper"
                  {...headProps}
                />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch
                  col="sides_per_file"
                  label="sides/file"
                  align="right"
                  {...headProps}
                />
              </TableCell>
              <TableCell className={classes.headCell}>
                <HeadSearch col="status" label="status" {...headProps} />
              </TableCell>
              {canEdit && (
                <TableCell className={classes.headCell} align="right">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((item) => (
              <TableRow
                key={item.key}
                hover
                className={item.status === 'pending' ? 'pendingRow' : ''}>
                {showTicks && (
                  <TableCell className={`${classes.cell} ${classes.frzSel}`}>
                    {item.status === 'pending' && (
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={ticked.includes(item.uuid)}
                        onChange={() => toggleTick(item.uuid)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell
                  className={`${classes.cell} ${classes.frz2}`}
                  style={{ left: selOffset }}>
                  <span className={classes.clamp} style={{ maxWidth: 132 }}>
                    {item.segment || '—'}
                  </span>
                </TableCell>
                <TableCell
                  className={`${classes.cell} ${classes.frz3}`}
                  style={{ left: 140 + selOffset }}
                  title={item.item_name || ''}>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      className={classes.clamp}
                      style={{ maxWidth: item.reference_link ? 198 : 224 }}>
                      {item.item_name}
                    </span>
                    {item.reference_link && (
                      <Link
                        href={item.reference_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: 6, flex: 'none' }}>
                        ↗
                      </Link>
                    )}
                      {item.file_link && (
                        <Link
                          href={item.file_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: 4, flex: 'none' }}
                        >
                          ↗
                        </Link>
                      )}
                  </span>
                </TableCell>
                <TableCell className={classes.cell}>
                  {item.reference_id || '—'}
                </TableCell>
                <TableCell className={classes.cell}>
                  {item.category || '—'}
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  {item.status !== 'base'
                    ? item.default_qty
                      ? item.default_qty
                      : '—'
                    : item.qty_norm != null
                    ? Number(item.qty_norm)
                    : item.qty_note || '—'}
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  {item.ppp != null && item.ppp !== ''
                    ? Number(item.ppp)
                    : '—'}
                </TableCell>
                <TableCell
                  className={`${classes.cell} ${classes.costCell}`}
                  align="right"
                  title={
                    rowCost(item) == null
                      ? item.ppp != null && item.ppp !== ''
                        ? 'Rated, but no quantity to cost it on'
                        : ''
                      : costWorking(item)
                  }>
                  {rowCost(item) != null ? rupees(rowCost(item)) : '—'}
                </TableCell>
                <TableCell className={classes.cell} title={item.vendor || ''}>
                  <span className={classes.clamp} style={{ maxWidth: 120 }}>
                    {item.vendor || '—'}
                  </span>
                </TableCell>
                <TableCell className={classes.cell}>
                  {item.capex_opex || '—'}
                </TableCell>
                <TableCell className={classes.cell}>
                  {item.item_type || '—'}
                </TableCell>
                <TableCell
                  className={classes.cell}
                  title={item.print_specification || ''}>
                  <span className={classes.clamp} style={{ maxWidth: 220 }}>
                    {item.print_specification || '—'}
                  </span>
                </TableCell>
                <TableCell
                  className={classes.cell}
                  title={item.paper_kind || ''}>
                  <span className={classes.clamp} style={{ maxWidth: 150 }}>
                    {item.paper_kind || '—'}
                  </span>
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  {item.sides_per_file != null && item.sides_per_file !== ''
                    ? item.sides_per_file
                    : '—'}
                </TableCell>
                <TableCell className={classes.cell}>
                  <StatusChip status={item.status} classes={classes} />
                </TableCell>
                <TableCell
                  className={`${classes.cell} ${classes.actionsCell}`}
                  align="right">
                  {canEdit && isCentral && item.status === 'pending' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setReviewItem(item)}
                      style={{ marginRight: 6 }}>
                      Review
                    </Button>
                  )}
                  {canEdit && item.uuid && (
                    <Button size="small" onClick={() => setEditItem(item)}>
                      Edit
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    title="Where this item came from"
                    onClick={(e) => setInfo({ item, anchor: e.currentTarget })}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell
                  colSpan={showTicks ? 15 : 14}
                  className={classes.cell}>
                  <Typography variant="body2" className={classes.muted}>
                    No items match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      )}

      {/* Option B: the total follows the filter. Unfiltered it is the whole
          catalogue; type a kit into the segment box and it is that kit; type a
          budget head and it is that head. One number, one place, every slice. */}
      <Box className={classes.totalBar}>
        <span>
          {view === 'kits'
            ? kitsShown.length === kitRollup.length
              ? `${kitRollup.length} kits`
              : `${kitsShown.length} of ${kitRollup.length} kits`
            : rows.length === all.length
            ? `${all.length} items`
            : `${rows.length} of ${all.length} items`}
        </span>
        <span className={classes.muted}>│</span>
        {/* Said once, for the whole view, instead of a column on every row:
            until every item carries a rate, the total below is a floor. */}
        <span
          className={shownCostable < shownItems ? classes.partial : undefined}>
          {shownCostable < shownItems
            ? `${shownItems - shownCostable} still need a rate or qty`
            : 'every item costed'}
        </span>
        <span className={classes.spacer} />
        <span className={classes.muted}>total</span>
        <span className={classes.totalBig}>{`₹${rupees(shownCost)}`}</span>
      </Box>

      {/* What the Source column used to say, plus who touched the item — off a
          ⓘ at the end of the row instead of a column every row had to carry. */}
      <Popover
        open={!!info}
        anchorEl={info ? info.anchor : null}
        onClose={() => setInfo(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {info && (
          <Box p={2} style={{ maxWidth: 320 }}>
            <Typography variant="subtitle2" gutterBottom>
              {info.item.item_name}
            </Typography>
            <Typography variant="body2" className={classes.muted}>
              Comes from <b>{info.item.source || '—'}</b>
            </Typography>
            {info.item.added_by_name && (
              <Typography variant="body2" className={classes.muted}>
                Added by <b>{info.item.added_by_name}</b>
              </Typography>
            )}
            {info.item.reviewed_by_name && (
              <Typography variant="body2" className={classes.muted}>
                Reviewed by <b>{info.item.reviewed_by_name}</b>
                {info.item.reviewed_at
                  ? ` on ${String(info.item.reviewed_at).slice(0, 10)}`
                  : ''}
              </Typography>
            )}
            {info.item.modified && (
              <Typography variant="body2" className={classes.muted}>
                Last changed {String(info.item.modified).slice(0, 10)}
              </Typography>
            )}
            {info.item.review_note && (
              <Typography variant="body2" className={classes.muted}>
                Note: {info.item.review_note}
              </Typography>
            )}
          </Box>
        )}
      </Popover>

      <AddItemDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={loadAdditions}
        segmentOptions={segmentOptions}
        vendorOptions={vendorNames}
      />
      <ReviewDialog
        item={reviewItem}
        onClose={() => setReviewItem(null)}
        onReviewed={loadAdditions}
      />
      <BulkApproveDialog
        open={bulkOpen}
        items={tickedItems}
        onClose={() => setBulkOpen(false)}
        onDone={() => {
          setTicked([])
          loadAdditions()
        }}
      />
      <EditDialog
        item={editItem}
        onClose={() => setEditItem(null)}
        onSaved={loadAdditions}
        segmentOptions={segmentOptions}
        vendorOptions={vendorNames}
      />
    </Box>
  )
}

export default CatalogueTab
